import { Observable, catchError, map, of } from 'rxjs'
import { Injectable } from '@angular/core'

import { ICustomerList } from '@modules/private/customer/data/interfaces/icustomer.interface'
import { IResponseArray } from '@data/interfaces/iresponseArray.interface'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

// Interfaz para la respuesta de nombres de clientes
export interface IClientNamesResponse {
  error: boolean
  msg: string
  data: Array<{
    _id: string
    name: string
  }>
}

@Injectable({
  providedIn: 'root',
})
export class ApiCustomerService extends ApiClass {
  constructor() {
    super()
    this.path = 'clients'
  }

  /**
   * Pide la CIEC en claro. Endpoint aparte a propósito: la credencial NO viaja en el detalle
   * del cliente, así que sólo la recibe quien tenga `clients.view-ciec` y cada consulta queda
   * registrada en el backend. Ver ADR-0032.
   */
  revealCiec(clientId: string) {
    return this.getCollections({}, environment.apiUrl, `clients/${clientId}/ciec`)
  }

  private cleanCustomerData(data: any): any {
    const cleanData = { ...data }

    // Lógica para Persona Moral
    if (cleanData.entity === 'Moral') {
      if (cleanData.sameLegalAddress) {
        // Si sameLegalAddress es true, eliminar legalAddress
        delete cleanData.legalAddress
      } else {
        // Si sameLegalAddress es false, asegurar que legalAddress existe y tiene datos
        if (!cleanData.legalAddress || Object.keys(cleanData.legalAddress).length === 0) {
          console.warn('legalAddress requerido cuando sameLegalAddress es false')
        }
      }
    } else {
      // Para Persona Física, siempre eliminar legalAddress y sameLegalAddress
      delete cleanData.legalAddress
      delete cleanData.sameLegalAddress
    }

    return cleanData
  }

  getAll(page = 1, limit = 10, filters = {}) {
    this.path = 'clients'
    const response: ICustomerList = { error: true, msg: '', data: [], total: 0, status: 200, page: 1, itemsByPage: 10, totalItems: 0 }

    // const populate = [{ path: 'docs' }]

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => r as ICustomerList),
      catchError(() => of(response)),
    )
  }

  create(data: any) {
    this.path = 'clients'
    const cleanData = this.cleanCustomerData(data)

    return this.postCollection(cleanData, 'create').pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  getById(id: string) {
    this.path = 'clients'
    return this.getCollection(id)
      .pipe(map((r) => r as { error: boolean; msg: string; data: any }))
      .pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  /**
   * POST /clients/:id/send-access — (re)genera contraseña temporal + manda correo REGISTER.
   * Devuelve `{ emailSent, emailError?, tempPassword, loginUsername }` en `data`.
   */
  sendAccess(id: string) {
    const fallback = { error: true, msg: 'No se pudieron enviar los accesos', data: null }
    return this.http.post<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}/send-access`, {}).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => {
        console.error('Error al enviar accesos:', e)
        return of({ ...fallback, msg: e?.error?.message ?? e?.error?.msg ?? fallback.msg })
      }),
    )
  }

  getClientNames() {
    const response: IClientNamesResponse = {
      error: true,
      msg: 'Error al obtener nombres de clientes',
      data: [],
    }

    return this.http.get<IClientNamesResponse>(`${environment.apiUrl}clients/names`).pipe(
      map((r) => r as IClientNamesResponse),
      catchError((e) => {
        console.error('Error al obtener nombres de clientes:', e)
        return of(response)
      }),
    )
  }

  update(id: string, data: any) {
    this.path = 'clients'
    const cleanData = this.cleanCustomerData(data)

    return this.putCollection(cleanData, undefined, 'clients/' + id).pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  /**
   * PUT /clients/:id/guarantors — reemplaza el conjunto de obligados solidarios (plan v4.3 §6.3): con `id` actualiza,
   * sin `id` crea y el que falta se quita. El `PUT /clients/:id` genérico no los toca. Conserva el `status` y el índice
   * del obligado que rompió una regla (`detail.index`) para decir en el diálogo a cuál se refiere.
   */
  updateGuarantors(id: string, guarantors: Record<string, string>[]) {
    return this.http.put<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}/guarantors`, { guarantors }).pipe(
      map((r) => ({ error: false, msg: r?.msg ?? '', data: r?.data ?? null, status: 200, index: null as number | null })),
      catchError((e) => {
        const body = e?.error ?? {}
        const index = typeof body?.detail?.index === 'number' ? body.detail.index : null
        return of({ error: true, msg: body?.msg ?? body?.message ?? '', data: body?.data ?? null, status: e?.status ?? 0, index })
      }),
    )
  }

  /**
   * DELETE /clients/:id — soft-delete del cliente + cascada a sus cotizaciones (solo Admin).
   * Preserva el `status` HTTP para que el caller distinga el **409** (el cliente tiene
   * arrendamientos y no puede borrarse → ofrecer desactivar) del resto de errores.
   * No usa `deleteCollection` heredado porque éste descarta el status HTTP.
   */
  deleteClient(id: string) {
    const fallback = { error: true, msg: 'No se pudo eliminar el cliente', status: 0 }
    return this.http.delete<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}`).pipe(
      map((r) => ({ error: r.error, msg: r.msg, status: 200 })),
      catchError((e) => of({ ...fallback, status: e?.status ?? 0, msg: e?.error?.msg ?? e?.error?.message ?? fallback.msg })),
    )
  }

  /** POST /clients/:id/deactivate — status→Inactive (bloquea acceso al portal). Solo Admin. */
  deactivate(id: string) {
    const fallback = { error: true, msg: 'No se pudo desactivar el cliente', data: null }
    return this.http.post<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}/deactivate`, {}).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ ...fallback, msg: e?.error?.msg ?? e?.error?.message ?? fallback.msg })),
    )
  }

  /** POST /clients/:id/reactivate — status→Active. Solo Admin. */
  reactivate(id: string) {
    const fallback = { error: true, msg: 'No se pudo reactivar el cliente', data: null }
    return this.http.post<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}/reactivate`, {}).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ ...fallback, msg: e?.error?.msg ?? e?.error?.message ?? fallback.msg })),
    )
  }

  /**
   * POST /clients/:id/reassign — reasigna vendedor (seller) y/o broker. Solo Admin.
   * `broker: null` quita el broker; omitir un campo lo deja igual. `referredBy` es inmutable.
   */
  reassign(id: string, payload: { seller?: string; broker?: string | null }) {
    const fallback = { error: true, msg: 'No se pudo reasignar el cliente', data: null }
    return this.http.post<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}clients/${id}/reassign`, payload).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ ...fallback, msg: e?.error?.msg ?? e?.error?.message ?? fallback.msg })),
    )
  }

  /**
   * POST /users — trae los usuarios internos (rol populado) para poblar los pickers de
   * vendedores/brokers (Admin). La respuesta es paginada anidada: `r.data.data` es el array
   * (mismo shape que consume main-table). El filtrado por rol se hace client-side (set chico).
   */
  getInternalUsers() {
    return this.http.post<any>(`${environment.apiUrl}users`, { page: 1, limit: 1000, filters: {} }).pipe(
      map((r) => (Array.isArray(r?.data?.data) ? r.data.data : [])),
      catchError(() => of([] as any[])),
    )
  }

  /**
   * Calificación A-D y métricas de comportamiento de pago del cliente, agregadas sobre todos sus
   * arrendamientos (F39). Vive en `clients/:id/payment-quality` aunque la sirva el módulo de pagos.
   */
  getPaymentQuality(clientId: string): Observable<{ error: boolean; msg: string; data: any }> {
    return this.http.get(`${environment.apiUrl}clients/${clientId}/payment-quality`).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo leer el comportamiento de pago', data: null })),
    )
  }
}
